/**
 * A HOC that renders a ModuleList for /donors.
 */

import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {
    RoughCircledBox,
    RoughHighlight,
    RoughUnderline,
} from "@khanacademy/khannotations";
import {View} from "@khanacademy/wonder-blocks-core";
import {RequestInfo} from "@khan/shared-core";

import PageRoot from "#app-shell-package/page-root.tsx";
import {getKaid} from "#data-access/profile-utils.ts";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import getClassyCampaignURL from "#donate-package/classy.ts";
import {MarketingColors} from "#marketing-content-shared-package/constants.ts";
import Headline from "#marketing-content-shared-package/headline.tsx";
import InfoBlockGroup from "#marketing-content-shared-package/info-block-group.tsx";
import InfoBlock from "#marketing-content-shared-package/info-block.tsx";
import Infographic from "#marketing-content-shared-package/infographic.tsx";
import LastChance from "#marketing-content-shared-package/last-chance.tsx";
import List from "#marketing-content-shared-package/list.tsx";
import * as mediaQueries from "#marketing-content-shared-package/media-queries.ts";
import ScrollButton from "#marketing-content-shared-package/scroll-button.tsx";
import Testimonial from "#marketing-content-shared-package/testimonial.tsx";
import ModuleList from "#modules-package/module-list.tsx";
import type {Module} from "#modules-package/types.ts";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

import {withStrings} from "./locales/donors-landing-page-module-list/load-strings.tsx";
import Media from "./media.tsx";
import Hero from "./modules/hero.tsx";

// A collection of all possible /donors Landing Page modules
const donorsLandingModules = {
    Headline,
    Hero,
    ScrollButton,
    List,
    InfoBlock,
    InfoBlockGroup,
    Infographic,
    LastChance,
    Testimonial,
} as const;

const heroModule = {kind: "Hero"} as const;

const styles = StyleSheet.create({
    donors: {
        // The blobs and collages intentionally render beyond the width of most displays.
        // Prevent that from expanding the screen width.
        overflowX: "hidden",
    },
    statHighlight: {
        display: "block",
        fontFamily: "Lato, sans-serif",
        fontSize: 70,
        fontWeight: 900,
        lineHeight: "48px",
        marginBottom: 4,
        color: MarketingColors.highlightTeal,

        [mediaQueries.largePhoneOrSmaller]: {
            fontSize: 40,
            lineHeight: "24px",
        },
    },
});

const highlightStyle = {
    roughness: 2,
    fill: MarketingColors.highlightTeal,
} as const;

const speedAnimation = {
    animation: "speed",
    speed: 5,
    delay: 200,
} as const;

const circleStyle = {
    roughness: 2,
    stroke: MarketingColors.highlightTeal,
    strokeWidth: 2,
} as const;

const speedAnimationHero1 = {
    animation: "speed",
    speed: 10,
    delay: 1200,
} as const;

const underlineStyleHero1 = {
    roughness: 0,
    stroke: MarketingColors.highlightTeal,
    strokeWidth: 2,
    bowing: {
        bowing: "inverse",
        ratio: 100,
    },
    lift: 4,
} as const;

const infographicStats = {
    learners: 169,
    practiceQuestions: 15,
    countries: 190,
    languages: 50,
} as const;

const qualityEducationModule = (profile: null | ProfileModelType) => ({
    kind: "InfoBlock",
    headline: (
        <Trans>
            Provide a free, high-quality education for{" "}
            <RoughUnderline
                animation={speedAnimationHero1}
                roughStyle={underlineStyleHero1}
            >
                anyone, anywhere
            </RoughUnderline>{" "}
            in the world
        </Trans>
    ),
    copy: t`With a low-cost mobile phone, a learner anywhere in the world has access to the same Khan Academy lessons as students in the world’s best schools. Help bring more courses, translated into more languages, to students ready to learn.`,
    cta: {
        copy: t`Support learning everywhere`,
        url: getClassyCampaignURL(RequestInfo.kaLocale, {
            c_src: getKaid(profile),
            c_src2: "Webapp.donorlandingqualityeducation",
        }),
        newWindow: true,
        type: "button",
        conversion: "donor_landing_click_learning_everywhere",
    },
    illustration: "WorldCollage",
});

const everyClassroomModule = (profile: null | ProfileModelType) => ({
    kind: "InfoBlock",
    headline: (
        <Trans>
            Bring world-class learning to{" "}
            <RoughHighlight
                roughStyle={highlightStyle}
                animation={speedAnimation}
            >
                every classroom
            </RoughHighlight>
        </Trans>
    ),
    copy: t`Every student deserves to step into a classroom equipped with the best resources. Help give teachers free, powerful tools and lessons that enable all students to reach their full potential.`,
    cta: {
        copy: t`Support every classroom`,
        url: getClassyCampaignURL(RequestInfo.kaLocale, {
            c_src: getKaid(profile),
            c_src2: "Webapp.donorlandingeveryclassroom",
        }),
        newWindow: true,
        type: "button",
        conversion: "donor_landing_click_every_classroom",
    },
    illustration: "EveryClassroomCollage",
});

const satModule = (profile: null | ProfileModelType) => ({
    kind: "InfoBlock",
    headline: (
        <Trans>
            <RoughUnderline
                animation={speedAnimationHero1}
                roughStyle={underlineStyleHero1}
            >
                Equalize college access
            </RoughUnderline>{" "}
            with free, official practice for the SAT
        </Trans>
    ),
    copy: t`Success on high-stakes exams shouldn’t depend on your family’s ability to pay for test prep. Khan Academy’s free SAT practice helps students raise their scores, independent of their ethnicity or family income. Help today’s hardest-working high school students achieve their college dreams.`,
    cta: {
        copy: t`Support college access`,
        url: getClassyCampaignURL(RequestInfo.kaLocale, {
            c_src: getKaid(profile),
            c_src2: "Webapp.donorlandingsat",
        }),
        newWindow: true,
        type: "button",
        conversion: "donor_landing_click_college_access",
    },
    illustration: "EqualizeAccessCollage",
});

const everyChildModule = (profile: null | ProfileModelType) => ({
    kind: "InfoBlock",
    headline: (
        <Trans>
            Make sure{" "}
            <RoughCircledBox
                delay={200}
                roughStyle={circleStyle}
                marginLeft={20}
                marginRight={20}
                marginTop={-10}
                marginBottom={0}
            >
                every child
            </RoughCircledBox>{" "}
            is ready for kindergarten
        </Trans>
    ),
    copy: t`No child should start kindergarten already behind their peers. Khan Kids is our free app—with no ads—that sparks a lifelong love of learning in every child, rich or poor.`,
    cta: {
        copy: t`Support early learning`,
        url: getClassyCampaignURL(RequestInfo.kaLocale, {
            c_src: getKaid(profile),
            c_src2: "Webapp.donorlandingeverychild",
        }),
        newWindow: true,
        type: "button",
        conversion: "donor_landing_click_early_learning",
    },
    illustration: "EveryChildCollage",
});

const howYouCanHelpModuleBlocks = (profile: null | ProfileModelType) => {
    const moduleList = [
        qualityEducationModule(profile),
        everyClassroomModule(profile),
    ];

    if (
        RequestInfo.remoteCountry === "US" ||
        RequestInfo.remoteCountry === "IN"
    ) {
        moduleList.push(satModule(profile));
    }

    if (RequestInfo.kaLocale === "en") {
        moduleList.push(everyChildModule(profile));
    }

    return moduleList;
};

const howYouCanHelpModule = (profile: null | ProfileModelType) => ({
    kind: "InfoBlockGroup",
    headline: t`How can you help level the playing field?`,
    blocks: howYouCanHelpModuleBlocks(profile),
    startingAlignment: "left",
});

const levelPlayingFieldModule = (profile: null | ProfileModelType) => ({
    kind: "InfoBlock",
    headline: (
        <Trans>
            <RoughUnderline
                animation={speedAnimationHero1}
                roughStyle={underlineStyleHero1}
            >
                Level
            </RoughUnderline>{" "}
            the playing field
        </Trans>
    ),
    copy: t`Millions of students lack opportunity. You can give it to them. Just $10 a month can change a life.`,
    cta: {
        copy: t`Give today`,
        type: "button",
        url: getClassyCampaignURL(RequestInfo.kaLocale, {
            c_src: getKaid(profile),
            c_src2: "Webapp.donorlandinglevelplaying",
        }),
        newWindow: true,
        conversion: "donor_landing_click_give_today",
    },
    illustration: "LevelPlayingFieldCollage",
    align: "right",
});

const modules: (arg1: null | ProfileModelType) => Array<Module> = (
    profile: null | ProfileModelType,
) => [
    heroModule,
    {
        kind: "ScrollButton",
        // These two properties need to match the properties of the
        // module you want the scroll button to scroll to.
        copy: t`Jump to: About Khan Academy`,
        scrollToSelector: '[data-scroll-id="about-khan-academy"]',
        alternateBackground: false,
    },
    {
        kind: "Headline",
        headline: t`Everyone is born with a desire to learn. They only need the chance.`,
        scrollId: "about-khan-academy",
    },
    {
        kind: "Testimonial",
        quote: t`“Khan Academy gave me a reason to wake up hopeful for a new day. Had I never come across it, my life story would be like millions of other Afghan girls stuck in the cage of ignorance. Thank you for helping me free that cage.”`,
        attribution: "SULTANA",
        meta: t`AFGHANISTAN`,
        backgroundIllustration: "BackgroundBlob",
    },
    {
        kind: "Infographic",
        headline: t`You can bring education to the world.`,
        staticGraphic: "/images/lohp/bring-education-2x.jpg",
        imageStyles: "bringEducation",
        stats: [
            <Trans key="1">
                <span className={css(styles.statHighlight)}>
                    {infographicStats.learners}+ million
                </span>{" "}
                people learning with us
            </Trans>,
            <Trans key="2">
                <span className={css(styles.statHighlight)}>
                    {infographicStats.practiceQuestions} billion
                </span>{" "}
                practice questions completed
            </Trans>,
            <Trans key="3">
                <span className={css(styles.statHighlight)}>
                    {infographicStats.countries}
                </span>{" "}
                countries
            </Trans>,
            <Trans key="4">
                <span className={css(styles.statHighlight)}>
                    {infographicStats.languages}+
                </span>{" "}
                languages
            </Trans>,
        ],
    },
    howYouCanHelpModule(profile),
    {
        kind: "Testimonial",
        quote: t`“It is rare that you can reach a lot of people and reach them deeply. Khan Academy’s impact has both breadth and depth. That is why I donate.”`,
        attribution: "CHUCK KUNG",
        meta:
            RequestInfo.remoteCountry === "US"
                ? t`Palo Alto, CA`
                : t`United States`,
        staticGraphic: "/images/lohp/chuck-2x.png",
        backgroundIllustration: "BackgroundBlob",
    },
    {
        kind: "List",
        headline: t`Learn more about Khan Academy`,
        listItems: [
            {
                kind: "ListItem",
                headline: t`Meet our leadership team`,
                copy: t`Learn more about our leadership team’s roles, backgrounds, and how they are changing the world of education.`,
                illustration: "LeadershipIcon",
                link: {
                    href: "/about/our-board",
                    copy: t`Meet our leadership team`,
                    // this page does not support client-side navigation
                },
            },
            {
                kind: "ListItem",
                headline: t`Read our annual report`,
                copy: t`We’re grateful for our donors and honored to share the work they make possible. We’re also excited to share the success of students and teachers using Khan Academy.`,
                illustration: "AnnualReportIcon",
                link: {
                    href: "https://annualreport.khanacademy.org/",
                    copy:
                        RequestInfo.kaLocale === "en"
                            ? t`View our Annual Report`
                            : t`View our Annual Report (English)`,
                    external: true,
                    openNewWindow: true,
                },
            },
            {
                kind: "ListItem",
                headline: t`Become a corporate partner`,
                copy: t`Inspire your employees to celebrate and support learning, and elevate your company’s role as a global leader in education.`,
                illustration: "PartnerIcon",
                link: {
                    href: "mailto:partnerships@khanacademy.org",
                    copy: t`Contact our partnership team`,
                    external: true,
                },
            },
        ],
    },
    levelPlayingFieldModule(profile),
];

type Props = WithUserProfileProps;

class DonorsLandingPageModuleList extends React.Component<Props> {
    render(): React.ReactNode {
        const {
            profile,
            profileError: _,
            profileLoading: __,
            ...rest
        } = this.props;

        const filteredModules = modules(profile).filter((module) => {
            return donorsLandingModules.hasOwnProperty(module.kind);
        });

        return (
            <PageRoot settings={{fullTitle: t`About Khan Academy`}}>
                <View style={styles.donors}>
                    <Media>
                        <ModuleList
                            {...rest}
                            domain="default"
                            modules={filteredModules}
                            moduleComponents={donorsLandingModules}
                        />
                    </Media>
                </View>
            </PageRoot>
        );
    }
}

type ExportProps = WithoutUserProfile<
    JSX.LibraryManagedAttributes<
        typeof DonorsLandingPageModuleList,
        React.ComponentProps<typeof DonorsLandingPageModuleList>
    >
>;

export default withStrings(
    withUserProfile(
        DonorsLandingPageModuleList,
    ) as React.ComponentType<ExportProps>,
);
